import React from "react";
import {InputAdornment, TextField, Tooltip} from "@material-ui/core";
import LockIcon from "@material-ui/icons/Lock";
import CountryFlag from "view/components/CountryFlag";
import type {ShowLanguage} from "view/components/SettingsRows/ShowLanguagesRow/useShowLanguages";
import AiTranslateButton from "./AiTranslateButton";
import useStyles from "./styles";

export const READ_ONLY_TRANSLATION_TOOLTIP =
  "The translation is managed in the library and can be edited by the Organiser Admin.";

type Props = {
  language: ShowLanguage;
  value: string;
  onChange: (value: string) => void;
  onTranslate: () => void;
  canTranslate: boolean;
  isTranslating: boolean;
  maxLength?: number;
  disabled?: boolean;
  /**
   * A translation owned elsewhere (a Library master shown on a Show): the
   * language name becomes the label, the value cannot change, and a lock
   * replaces the AI action (FS-2999 §4).
   */
  readOnly?: boolean;
};

const LocalisationTextRow = ({
  language,
  value,
  onChange,
  onTranslate,
  canTranslate,
  isTranslating,
  maxLength,
  disabled,
  readOnly,
}: Props) => {
  const classes = useStyles();

  return (
    <TextField
      fullWidth
      variant="outlined"
      label={readOnly ? language.name : undefined}
      placeholder={readOnly ? undefined : language.name}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      className={classes.languageRow}
      InputProps={{
        className: classes.languageRow,
        readOnly,
        inputProps: {
          maxLength,
          "aria-label": `${language.name} translation`,
          ...(readOnly && {"aria-readonly": true}),
        },
        startAdornment: (
          <InputAdornment position="start">
            <CountryFlag flagEmoji={language.flagEmoji} />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            {readOnly ? (
              <Tooltip title={READ_ONLY_TRANSLATION_TOOLTIP}>
                <LockIcon
                  fontSize="small"
                  color="disabled"
                  data-testid="localisation-lock"
                />
              </Tooltip>
            ) : (
              <AiTranslateButton
                onClick={onTranslate}
                disabled={!canTranslate || disabled}
                isLoading={isTranslating}
                tooltip={
                  canTranslate
                    ? `Generate the ${language.name} translation with AI`
                    : "Add the English (UK) text first"
                }
              />
            )}
          </InputAdornment>
        ),
      }}
      // Floating label rows always look filled: the flag adornment sits where
      // a collapsed label would.
      InputLabelProps={readOnly ? {shrink: true} : undefined}
    />
  );
};

export default LocalisationTextRow;
